import { useUsageLimitReason, useUserInfo } from "@/api/user";
import { EndTrialDialog } from "@/components/billing/EndTrialDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { useStartPaymentMethodSetup } from "@/hooks/useStartPaymentMethodSetup";
import { isRealNativePlatform } from "@/native/platform";
import * as analytics from "@/utils/analytics";
import { ExternalLink } from "lucide-react";
import React, { useState } from "react";

interface UsageLimitBannerProps {
  className?: string;
}

export const UsageLimitBanner: React.FC<UsageLimitBannerProps> = ({
  className = "",
}) => {
  const isOrgAdmin = useIsOrgAdmin();
  const { reason, hasPaymentMethod } = useUsageLimitReason();
  const { data: userInfo } = useUserInfo();
  const openSettings = useOpenSettings();
  const startPaymentMethodSetup = useStartPaymentMethodSetup();
  const [showEndTrialDialog, setShowEndTrialDialog] = useState(false);

  const subscription = userInfo?.subscription;

  // Billing UI is hidden only in the real iOS container (Apple 3.1.1).
  if (isRealNativePlatform()) {
    return null;
  }

  // Don't render if no usage limit
  if (reason === "none") {
    return null;
  }

  const currentPeriodEnd = subscription?.current_period_end;
  const formattedPeriodEndDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // Generate message based on reason
  const getMessage = () => {
    switch (reason) {
      case "trial_credits_exceeded":
        if (isOrgAdmin) {
          if (hasPaymentMethod) {
            return (
              <>
                <strong>Usage limit reached.</strong> You've reached 100% of
                your included credits during your free trial. End your trial now
                to continue using the app.
              </>
            );
          }
          return (
            <>
              <strong>Usage limit reached.</strong> You've reached 100% of your{" "}
              included credits during your free trial. Add a payment method to
              continue using the app.
            </>
          );
        } else {
          return (
            <>
              <strong>Usage limit reached.</strong> Your organization has
              reached 100% of your included credits during the free trial.
              Please reach out to an administrator to{" "}
              {hasPaymentMethod ? "end the trial" : "add a payment method"}.
            </>
          );
        }

      case "overage_limit_exceeded":
        if (isOrgAdmin) {
          return (
            <>
              <strong>Usage limit reached.</strong> Your organization has
              exceeded the on-demand spending limit for this billing period.
              Update your on-demand limit in billing settings to continue using
              the app.
            </>
          );
        } else {
          return (
            <>
              <strong>Usage limit reached.</strong> Your organization has
              exceeded the on-demand spending limit for this billing period.
              Please reach out to an administrator to increase the limit.
            </>
          );
        }

      default:
        return (
          <>
            <strong>Usage limit reached.</strong> Your usage has been
            temporarily restricted.{" "}
            {formattedPeriodEndDate && (
              <>Usage will resume on {formattedPeriodEndDate}.</>
            )}
          </>
        );
    }
  };

  // Determine button text and action
  const getButtonConfig = (): {
    text: string;
    onClick: () => void;
  } | null => {
    if (!isOrgAdmin) return null;

    const goToBilling = () => openSettings(ROUTES.SETTINGS.USAGE_BILLING);

    switch (reason) {
      case "trial_credits_exceeded":
        // A card is already on file — offer to end the trial (and start
        // billing) instead of collecting a payment method.
        if (hasPaymentMethod) {
          return {
            text: "End trial now",
            onClick: () => setShowEndTrialDialog(true),
          };
        }
        return {
          text: "Add Payment Method",
          onClick: () => startPaymentMethodSetup(),
        };
      case "overage_limit_exceeded":
        return {
          text: "Update On-Demand Limit",
          onClick: goToBilling,
        };
      default:
        return {
          text: "Manage Billing",
          onClick: goToBilling,
        };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <Alert
      className={`dark bg-background relative border-0 border-b-1 rounded-none py-4 px-4 md:px-8 ${className}`}
    >
      <AlertDescription className="text-sm text-white flex gap-2 items-center lg:items-center flex-col lg:flex-row lg:justify-between h-full">
        <span>
          <span className="mr-2">{getMessage()}</span>
          {buttonConfig && (
            <button
              type="button"
              onClick={() => {
                analytics.trackBillingUsageLimitActionClicked({
                  limitReason: reason,
                  actionType: buttonConfig.text,
                });
                buttonConfig.onClick();
              }}
              className="cursor-pointer whitespace-nowrap text-rust hover:underline"
            >
              {buttonConfig.text}
              <ExternalLink className="ml-1 relative bottom-[1px] inline w-3.5 h-3.5" />
            </button>
          )}
        </span>
      </AlertDescription>
      <EndTrialDialog
        open={showEndTrialDialog}
        onOpenChange={setShowEndTrialDialog}
      />
    </Alert>
  );
};
